/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import msdyn365Commerce from '@msdyn365-commerce/core';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { IProductComparisonProductMainInfo, IProductComparisonViewProps, ProductComparison } from './product-comparison';
import { AttributeDataType } from '@msdyn365-commerce/retail-proxy';

/**
 * Renders dynamic header values.
 * @param values - cd..info.
 * @param headerName - Header Name.
 * @returns List of react nodes.
 */
const renderDynamicHeaderCells = (
    values: {
        productMainInfo: IProductComparisonProductMainInfo;
        node: React.ReactNode;
    }[],
    headerName: string,
    currentClickerPosition?: number
) => {
    return values.map((value, index) => {
        if (index === currentClickerPosition) {
            return (
                <Node
                    key={value.productMainInfo.tableHeaderProps.key}
                    {...value.productMainInfo.tableHeaderProps}
                    role='menuitem'
                    tabindex='0'
                    style={value.productMainInfo.cellContainerProps.style}
                >
                    {headerName}
                </Node>
            );
        }
        return <Node key={index} {...value.productMainInfo.tableHeaderProps} style={value.productMainInfo.cellContainerProps.style}></Node>;
    });
};

/**
 * Renders dynamic cell values.
 * @param values - Cells info.
 * @param hoveringCellItemId - Currently hovered cell.
 * @returns List of react nodes.
 */
const renderDynamicCells = (
    values: {
        productMainInfo: IProductComparisonProductMainInfo;
        node: React.ReactNode;
    }[],
    hoveringCellItemId: string
) => {
    return values.map(value => {
        return (
            <Node
                {...value.productMainInfo.tableCellProps}
                key={`${value.productMainInfo.product.itemId}-${hoveringCellItemId}`}
                style={value.productMainInfo.cellContainerProps.style}
            >
                {value.node}
            </Node>
        );
    });
};
/**
 * Represents product comparison view.
 * @param props - View props for product comparison.
 * @returns Functional component of product comparison.
 */
export const ProductComparisonViewFunctionalComponent: React.FC<IProductComparisonViewProps> = props => {
    return (
        <Module {...props.moduleProps}>
            {props.backToShopping}
            {props.heading}
            <Node className={`${ProductComparison.className}__product_count_text`}>{props.productCountText}</Node>
            {props.isEmptyContent || props.isMainContentReady ? undefined : props.loading}
            {props.isEmptyContent ? props.emptyComparison : undefined}
            <Node {...props.actionBarContainerProps}>
                {!props.isEmptyContent && props.isMainContentReady ? props.clearAllButton : undefined}
            </Node>
            {props.isMainContentReady ? (
                <Node className={ProductComparison.mainContainerClassName}>
                    <Node className={ProductComparison.containerClassName}>
                        <Node className={ProductComparison.tableClassName} ref={props.tableReference}>
                            <Node className={`${ProductComparison.tableHeaderClassName}`}>
                                <Node
                                    className={`${ProductComparison.tableHeaderClassName}__header__container`}
                                    ref={props.tableHeaderReference}
                                >
                                    {props.shouldShowPreviousClicker && props.previousClicker}
                                    <Node
                                        className={classnames(
                                            ProductComparison.tableRowClassName,
                                            `${ProductComparison.tableHeaderClassName}__product-row`
                                        )}
                                        role='menu'
                                    >
                                        {props.productsMainInfo?.map(productMainInfo => {
                                            const isFromCurrentCatalog = productMainInfo.isFromCurrentCatalog;
                                            const actionsClassName = `${ProductComparison.productRowClassName}__actions`;
                                            return (
                                                <Node
                                                    {...productMainInfo.cellContainerProps}
                                                    key={productMainInfo.cellContainerProps.key as string}
                                                    style={productMainInfo.cellContainerProps.style}
                                                >
                                                    {productMainInfo.removeFromComparisonButton}
                                                    {productMainInfo.productImage}
                                                    {msdyn365Commerce.isBrowser ? productMainInfo.productLink : undefined}
                                                    {productMainInfo.productPrice}
                                                    <Node className={actionsClassName} key={productMainInfo.product.itemId}>
                                                        {isFromCurrentCatalog
                                                            ? productMainInfo.quickView
                                                            : productMainInfo.goToProductDetailsButton}
                                                    </Node>
                                                </Node>
                                            );
                                        })}
                                    </Node>
                                    {props.shouldShowNextClicker && props.nextClicker}
                                </Node>
                            </Node>
                            <Node
                                className={ProductComparison.tableBodyClassName}
                                onScroll={props.handleScroll}
                                ref={props.tableBodyReference}
                            >
                                {props.productsMainInfo?.map((rating, key) => {
                                    return rating.productRating !== undefined && key === 0 ? (
                                        <Node
                                            className={classnames(
                                                `${ProductComparison.tableBodyClassName}__rating-row`,
                                                ProductComparison.tableRowClassName
                                            )}
                                        >
                                            <Node
                                                className={ProductComparison.tableRowContentClassName}
                                                key={`rating-${props.currentClickerPosition}`}
                                                role='menu'
                                            >
                                                {props.productsMainInfo?.map((productMainInfo, index) => {
                                                    if (index === props.currentClickerPosition) {
                                                        return (
                                                            <Node
                                                                key={productMainInfo.tableHeaderProps.key}
                                                                {...productMainInfo.tableHeaderProps}
                                                                role='menuitem'
                                                                tabindex='0'
                                                                style={productMainInfo.cellContainerProps.style}
                                                            >
                                                                {props.resources.productComparisonProductItemRating}
                                                            </Node>
                                                        );
                                                    }
                                                    return (
                                                        <Node
                                                            key={productMainInfo.tableHeaderProps.key}
                                                            {...productMainInfo.tableHeaderProps}
                                                            role='none'
                                                            tabindex='-1'
                                                            style={productMainInfo.cellContainerProps.style}
                                                        ></Node>
                                                    );
                                                })}
                                            </Node>
                                            <Node
                                                className={`${ProductComparison.tableRowContentClassName} ${ProductComparison.tableRowContentInformationClassName}`}
                                                role='menu'
                                            >
                                                {props.productsMainInfo?.map(productMainInfo => {
                                                    return (
                                                        <Node
                                                            {...productMainInfo.tableCellProps}
                                                            key={`${productMainInfo.product.itemId}-${props.hoveringCellItemId!}`}
                                                            style={productMainInfo.cellContainerProps.style}
                                                        >
                                                            {productMainInfo.productRating}
                                                        </Node>
                                                    );
                                                })}
                                            </Node>
                                        </Node>
                                    ) : (
                                        undefined
                                    );
                                })}
                                {props.productsMainInfo?.map((description, key) => {
                                    return description.productDescription !== undefined && key === 0 ? (
                                        <Node
                                            className={classnames(
                                                `${ProductComparison.tableBodyClassName}__description-row`,
                                                ProductComparison.tableRowClassName
                                            )}
                                        >
                                            <Node
                                                className={ProductComparison.tableRowContentClassName}
                                                key={`description-${props.currentClickerPosition}`}
                                                role='menu'
                                            >
                                                {props.productsMainInfo?.map((productMainInfo, index) => {
                                                    if (index === props.currentClickerPosition) {
                                                        return (
                                                            <Node
                                                                key={productMainInfo.tableHeaderProps.key}
                                                                {...productMainInfo.tableHeaderProps}
                                                                role='menuitem'
                                                                tabindex='0'
                                                                style={productMainInfo.cellContainerProps.style}
                                                            >
                                                                {props.resources.productComparisonProductItemDescription}
                                                            </Node>
                                                        );
                                                    }
                                                    return (
                                                        <Node
                                                            key={productMainInfo.tableHeaderProps.key}
                                                            {...productMainInfo.tableHeaderProps}
                                                            role='none'
                                                            tabindex='-1'
                                                            style={productMainInfo.cellContainerProps.style}
                                                        ></Node>
                                                    );
                                                })}
                                            </Node>
                                            <Node
                                                className={`${ProductComparison.tableRowContentClassName} ${ProductComparison.tableRowContentInformationClassName}`}
                                                role='menu'
                                            >
                                                {props.productsMainInfo?.map(productMainInfo => {
                                                    return (
                                                        <Node
                                                            {...productMainInfo.tableCellProps}
                                                            key={`${productMainInfo.product.itemId}-${props.hoveringCellItemId!}`}
                                                            style={productMainInfo.cellContainerProps.style}
                                                        >
                                                            {productMainInfo.productDescription}
                                                        </Node>
                                                    );
                                                })}
                                            </Node>
                                        </Node>
                                    ) : (
                                        undefined
                                    );
                                })}
                                {props.dimensionsProps?.map(dimensionProps => {
                                    return (
                                        dimensionProps?.dimensionName && (
                                            <Node
                                                className={classnames(
                                                    ProductComparison.dimensionsRowClassName,
                                                    ProductComparison.tableRowClassName,
                                                    dimensionProps.dimensionClassName
                                                )}
                                                key={dimensionProps.dimensionClassName}
                                            >
                                                <Node
                                                    className={ProductComparison.tableRowContentClassName}
                                                    key={`${dimensionProps.dimensionName}-${props.currentClickerPosition}`}
                                                    role='menu'
                                                >
                                                    {renderDynamicHeaderCells(
                                                        dimensionProps.productDimensionValues,
                                                        dimensionProps.dimensionName,
                                                        props.currentClickerPosition
                                                    )}
                                                </Node>

                                                <Node
                                                    className={`${ProductComparison.tableRowContentClassName} ${ProductComparison.tableRowContentInformationClassName}`}
                                                    role='menu'
                                                >
                                                    {renderDynamicCells(dimensionProps.productDimensionValues, props.hoveringCellItemId!)}
                                                </Node>
                                            </Node>
                                        )
                                    );
                                })}
                                {props.specificationsProps?.map(specificationProps => {
                                    // check the boolean type attribute with single value is defined and get boolean value
                                    const attributeBooleanStringValue: string | undefined =
                                        specificationProps.specification.BooleanValue !== undefined &&
                                        specificationProps.specification.DataTypeValue === AttributeDataType.TrueFalse
                                            ? specificationProps.specification.BooleanValue.toString()
                                            : undefined;

                                    return StringExtensions.isNullOrEmpty(specificationProps.specification.TextValue) === false ||
                                        StringExtensions.isNullOrEmpty(attributeBooleanStringValue) === false ? (
                                        <Node
                                            className={classnames(
                                                ProductComparison.specificationsRowClassName,
                                                ProductComparison.tableRowClassName,
                                                specificationProps.specificationClassName
                                            )}
                                            key={specificationProps.specificationClassName}
                                        >
                                            <Node
                                                className={ProductComparison.tableRowContentClassName}
                                                key={`${specificationProps.specification.Name}-${props.currentClickerPosition}`}
                                                role='menu'
                                            >
                                                {renderDynamicHeaderCells(
                                                    specificationProps.specificationValues,
                                                    specificationProps.specification.Name!,
                                                    props.currentClickerPosition
                                                )}
                                            </Node>
                                            <Node
                                                className={`${ProductComparison.tableRowContentClassName} ${ProductComparison.tableRowContentInformationClassName}`}
                                                role='menu'
                                            >
                                                {renderDynamicCells(specificationProps.specificationValues, props.hoveringCellItemId!)}
                                            </Node>
                                        </Node>
                                    ) : (
                                        undefined
                                    );
                                })}
                                <Node
                                    className={classnames(
                                        `${ProductComparison.tableBodyClassName}__sku-row`,
                                        ProductComparison.tableRowClassName
                                    )}
                                >
                                    <Node
                                        className={ProductComparison.tableRowContentClassName}
                                        key={`sku-${props.currentClickerPosition}`}
                                        role='menu'
                                    >
                                        {props.productsMainInfo?.map((productMainInfo, index) => {
                                            if (index === props.currentClickerPosition) {
                                                return (
                                                    <Node
                                                        key={productMainInfo.tableHeaderProps.key}
                                                        {...productMainInfo.tableHeaderProps}
                                                        role='menuitem'
                                                        tabindex='0'
                                                        style={productMainInfo.cellContainerProps.style}
                                                    >
                                                        {props.resources.productComparisonProductItemSku}
                                                    </Node>
                                                );
                                            }
                                            return (
                                                <Node
                                                    key={productMainInfo.tableHeaderProps.key}
                                                    {...productMainInfo.tableHeaderProps}
                                                    style={productMainInfo.cellContainerProps.style}
                                                ></Node>
                                            );
                                        })}
                                    </Node>
                                    <Node
                                        className={`${ProductComparison.tableRowContentClassName} ${ProductComparison.tableRowContentInformationClassName}`}
                                        role='menu'
                                    >
                                        {props.productsMainInfo?.map(productMainInfo => {
                                            return (
                                                <Node
                                                    {...productMainInfo.tableCellProps}
                                                    key={`${productMainInfo.product.itemId}-${props.hoveringCellItemId!}`}
                                                    style={productMainInfo.cellContainerProps.style}
                                                >
                                                    {productMainInfo.product.productInfo.ItemId}
                                                </Node>
                                            );
                                        })}
                                    </Node>
                                </Node>
                            </Node>
                        </Node>
                    </Node>
                </Node>
            ) : (
                undefined
            )}
        </Module>
    );
};

export default ProductComparisonViewFunctionalComponent;
